import uniq from 'lodash/uniq'

import { createSelector } from '@app/utils/createSelector'
import { catchFetchDataError } from '@app/utils/performFetchData'

import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { createReduxSlice } from '@app/store/redux_slice'
import { profileUserResultSelector } from '@app/store/selectors/profile'

import { routingDateSelector } from './routing'

const requestsListSlice = createReduxSlice<string[]>('requests_list')

/** The first page replaces the list; later pages append. */
export const addRequestsPageSlice = (ids: string[], { page }: { page: number }) =>
  requestsListSlice.update(state => (page === 1 ? ids : uniq((state ?? EMPTY_LIST).concat(ids))))

const requestsWithDebtSlice = createReduxSlice<SliceState<string[]>>('requests_with_debt')

/** A repayment settles every debt the list held. */
export const clearRequestsWithDebtSlice = () => requestsWithDebtSlice.update(state => (state ? { ...state, data: EMPTY_LIST } : state))

export const requestsWithDebtSyncSlice = createReduxSlice<{ hash: string }>('requests_with_debt_sync')

export const requestsListIdsSelector = createSelector([requestsListSlice.selector], list => list ?? EMPTY_LIST)

export const requestsWithDebtIdsResultSelector = createSelector(
  [requestsWithDebtSlice.selector, requestsWithDebtSyncSlice.selector, profileUserResultSelector, routingDateSelector],
  (sliceState, sync, userResult, date) =>
    createLoadable(c => {
      const user = c.unwrap(userResult)
      if (user?.account_type !== 'parent') return EMPTY_LIST
      return withCheckedSliceState(c, {
        sliceState,
        slice: requestsWithDebtSlice,
        hash: [user.id, sync?.hash ?? ''].join(':'),
        date: { date, expiresInMinutes: 10 },
        load: async dispatch => {
          const [{ getRequestsWithDebt }, { callV2Api }] = await Promise.all([
            import('@app/packages/api/v2/actions/requests'),
            import('@app/store/actions/api'),
          ])
          const resp = await dispatch(callV2Api(getRequestsWithDebt())).catch(catchFetchDataError(dispatch, 'Requests with debt fetch failed'))
          return resp.data.map(req => req.id)
        },
      })
    })
)

const EMPTY_LIST: string[] = []
